// src/components/workspace/NotesIntelligence.js
import { useEffect, useRef } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

function NotesIntelligence({ problemId, notes, currentUser }) {
  const lastSummaryTimeRef = useRef(null);
  const processedNotesRef = useRef(new Set());

  useEffect(() => {
    if (!notes || notes.length === 0) return;

    const generateSummary = () => {
      // Get notes that haven't been processed
      const unprocessedNotes = notes.filter(
        (note) => !processedNotesRef.current.has(note.id)
      );

      if (unprocessedNotes.length === 0) return;

      // Mark notes as processed
      unprocessedNotes.forEach((note) => {
        processedNotesRef.current.add(note.id);
      });

      // Generate summary from notes
      const allNoteContent = notes
        .map((note) => note.content)
        .join(" ")
        .toLowerCase();

      // Extract key information
      const summaryParts = [];
      
      // Check for issue identification
      if (
        allNoteContent.includes("issue") ||
        allNoteContent.includes("problem") ||
        allNoteContent.includes("error")
      ) {
        summaryParts.push("Issue identification");
      }

      // Check for solution
      if (
        allNoteContent.includes("fix") ||
        allNoteContent.includes("solution") ||
        allNoteContent.includes("resolved")
      ) {
        summaryParts.push("Solution provided");
      }

      // Check for testing
      if (
        allNoteContent.includes("test") ||
        allNoteContent.includes("verify") ||
        allNoteContent.includes("check")
      ) {
        summaryParts.push("Testing/verification");
      }

      // Build summary text
      let summaryText = "Summary: ";
      if (summaryParts.length > 0) {
        summaryText += summaryParts.join(", ");
      } else {
        summaryText += "Notes updated with collaborative information.";
      }

      // Add note count
      summaryText += ` (${notes.length} note${notes.length !== 1 ? "s" : ""} total)`;

      // Post to updates collection
      const postSummary = async () => {
        try {
          await addDoc(collection(db, "problems", problemId, "updates"), {
            message: summaryText,
            type: "info",
            createdAt: serverTimestamp(),
            senderId: "system",
            isIntelligenceSummary: true,
          });
        } catch (err) {
          console.error("Error posting intelligence summary:", err);
        }
      };

      postSummary();
      lastSummaryTimeRef.current = new Date();
    };

    // Generate summary on mount or when notes change significantly
    const now = new Date();
    const shouldGenerate =
      !lastSummaryTimeRef.current ||
      now - lastSummaryTimeRef.current > 10 * 60 * 1000; // 10 minutes

    if (shouldGenerate) {
      generateSummary();
    }

    // Set up interval for auto-refresh every 10 minutes
    const interval = setInterval(() => {
      generateSummary();
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [notes, problemId]);

  return null; // This is a background component, no UI
}

export default NotesIntelligence;

